import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { FaSearch, FaTimes, FaSpinner, FaLock } from 'react-icons/fa';
import debounce from 'lodash/debounce';
import { getCurrentUser } from '../lib/auth';

interface SearchResult {
  id: string;
  type: 'song' | 'story';
  title?: string;
  creator?: string;
  category?: string;
  language?: string;
  created_at: string;
  likes?: number;
}

interface SearchBarProps {
  isMobile?: boolean;
  className?: string;
}

const SearchBar = ({ isMobile = false, className = '' }: SearchBarProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = getCurrentUser();

  // Check if user has access
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setHasAccess(false);
        return;
      }

      if (user.is_admin) {
        setHasAccess(true);
        return;
      }

      try {
        const { data: couponUsage, error } = await supabase
          .from('stripecouponusage')
          .select('*')
          .eq('user_id', user.id)
          .gte('valid_until', new Date().toISOString());

        setHasAccess(!!couponUsage && couponUsage.length > 0);
      } catch (error) {
        console.error('Error checking access:', error);
        setHasAccess(false);
      }
    };

    checkAccess();
  }, [user]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const performSearch = debounce(async (searchQuery: string) => {
    if (!searchQuery.trim() || !hasAccess) {
      setResults([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Search songs
      const { data: songs, error: songsError } = await supabase
        .from('stripesongs')
        .select('id, creator, language, song_likes, created_at')
        .or(`creator.ilike.%${searchQuery}%, song_lyrics.ilike.%${searchQuery}%`)
        .eq('is_visible', true)
        .limit(5);

      if (songsError) throw songsError;

      // Search stories
      const { data: stories, error: storiesError } = await supabase
        .from('stripeenstory')
        .select('id, title, category, created_at')
        .or(`title.ilike.%${searchQuery}%, content.ilike.%${searchQuery}%`)
        .limit(5);

      if (storiesError) throw storiesError;

      // Combine and format results
      const formattedResults: SearchResult[] = [
        ...(songs?.map(song => ({
          id: song.id,
          type: 'song' as const,
          creator: song.creator,
          language: song.language,
          likes: song.song_likes,
          created_at: song.created_at
        })) || []),
        ...(stories?.map(story => ({
          id: story.id,
          type: 'story' as const,
          title: story.title,
          category: story.category,
          created_at: story.created_at
        })) || [])
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setResults(formattedResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, 300);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(true);
    performSearch(value);
  };

  const handleResultClick = (result: SearchResult) => {
    setShowResults(false);
    setQuery('');
    setIsFocused(false);
    if (result.type === 'song') {
      navigate(`/songs/${result.id}`);
    } else {
      navigate(`/story/${result.id}`);
    }
  };

  

   if (!hasAccess) {
    return (
      <div className={`relative w-full ${className}`}>
        <div className="relative group">
          <input
            type="text"
            value=""
            disabled
            placeholder={isMobile ? "Search..." : "Purchase access to search"}
            className={`w-full pl-8 pr-4 py-2 rounded-xl border border-gray-600/30 bg-gray-800/50 backdrop-blur-lg text-gray-400 cursor-not-allowed transition-all duration-300 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaLock className="w-3 h-3 text-gray-500" />
          </div>
        </div>
      </div>
    );
  }
  

  return (
    <div ref={searchRef} className={`relative w-full ${className}`}>
      <div className="relative group">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            setShowResults(true);
            setIsFocused(true);
          }}
          onBlur={() => {
            // Delay hiding results to allow for clicks
            setTimeout(() => setIsFocused(false), 150);
          }}
          placeholder={isMobile ? "Search..." : "Search songs, stories..."}
          className={`w-full pl-8 pr-10 py-2 rounded-xl border border-gray-600/30 bg-gray-800/50 backdrop-blur-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 focus:bg-gray-800/70 transition-all duration-300 ${
            isMobile ? 'text-sm' : 'text-sm'
          } ${isFocused ? 'ring-2 ring-yellow-400/50 border-yellow-400/50' : ''}`}
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {loading ? (
            <FaSpinner className="w-3 h-3 text-yellow-400 animate-spin" />
          ) : (
            <FaSearch className={`w-3 h-3 transition-colors duration-300 ${
              isFocused ? 'text-yellow-400' : 'text-gray-400'
            }`} />
          )}
        </div>
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults([]);
              setShowResults(false);
              setIsFocused(false);
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-700/50 rounded-r-xl transition-colors duration-200"
          >
            <FaTimes className="w-3 h-3 text-gray-400 hover:text-white transition-colors duration-200" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && query && (
        <div className={`absolute z-50 top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-600/50 max-h-80 overflow-y-auto ${
          isMobile ? 'mx-0' : ''
        }`}>
          {loading ? (
            <div className="p-4 text-center">
              <FaSpinner className="w-4 h-4 text-yellow-400 animate-spin mx-auto mb-2" />
              <p className="text-gray-300 text-sm">Searching...</p>
            </div>
          ) : results.length > 0 ? (
            <div className="py-1">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-800/50 flex items-center justify-between transition-colors duration-200 border-b border-gray-800/30 last:border-b-0"
                >
                  <div>
                    <p className="text-sm font-medium text-white">
                      {result.type === 'song' ? result.creator : result.title}
                    </p>
                    <p className="text-xs text-gray-400">
                      {result.type === 'song' ? result.language : result.category}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${
                      result.type === 'song' 
                        ? 'bg-yellow-400/20 text-yellow-400' 
                        : 'bg-blue-400/20 text-blue-400'
                    }`}>
                      {result.type}
                    </span>
                    <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-3">
                <FaSearch className="w-5 h-5 text-gray-500" />
              </div>
              <p className="text-gray-400 text-sm">No results found</p>
              <p className="text-gray-500 text-xs mt-1">Try different keywords</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;